import { NameResult } from "../types/NameResult";

// Mock domain names pool for different keywords
const mockDomainNames = [
  "brandify", "snapnest", "quickflow", "zenith", "nexify",
  "luminate", "pivotly", "sparkwise", "cloudnest", "swiftbase",
  "mintify", "fluenthub", "beamly", "crestwave", "fusioniq",
  "prismly", "velocityai", "streamforge", "brightpath", "pulsehq",
  "eclipseio", "resonance", "elevateapp", "catalystiq", "harmonylab"
];

// Mock data for /generate-names endpoint
export const mockGenerateNames = (params: {
  keywords: string[];
  tone?: string;
  length?: string;
  tld?: string;
}): { names: NameResult[] } => {
  // Generate 15 names based on keywords
  const numResults = 15;
  const names: NameResult[] = [];
  
  for (let i = 0; i < numResults; i++) {
    // Use a combination of actual mock names and keyword-based variations
    let baseName: string;
    
    if (i < mockDomainNames.length) {
      baseName = mockDomainNames[i];
    } else {
      // Create variations using keywords
      const keyword = params.keywords[i % params.keywords.length] || "app";
      const suffixes = ["ly", "ify", "hub", "io", "ai", "hq", "app", "pro"];
      baseName = keyword.toLowerCase().replace(/\s+/g, "") + suffixes[i % suffixes.length];
    }
    
    // Generate random scores (1-5 scale)
    const brandability = Math.floor(Math.random() * 5) + 1;
    const relevance = Math.floor(Math.random() * 5) + 1;
    const availability = Math.floor(Math.random() * 5) + 1;
    const neutrality = Math.floor(Math.random() * 5) + 1;
    const differentiation = Math.floor(Math.random() * 5) + 1;
    const feel = Math.floor(Math.random() * 5) + 1;
    const internationality = Math.floor(Math.random() * 5) + 1;
    const technical_aesthetics = Math.floor(Math.random() * 5) + 1;
    
    const total_score = brandability + relevance + availability + neutrality + 
                        differentiation + feel + internationality + technical_aesthetics;
    
    let verdict = 'weak';
    if (total_score >= 32) verdict = 'strong';
    else if (total_score >= 24) verdict = 'moderate';
    
    names.push({
      name: baseName,
      available: Math.random() < 0.5, // Will be checked separately
      trademarkAvailable: Math.random() < 0.5,
      slogan: undefined,
      score: {
        name: baseName,
        scores: {
          brandability,
          relevance,
          availability,
          neutrality,
          differentiation,
          feel,
          internationality,
          technical_aesthetics,
        },
        total_score,
        rationale: {
          brandability: "Mock rationale for brandability",
          relevance: "Mock rationale for relevance",
          availability: "Mock rationale for availability",
          neutrality: "Mock rationale for neutrality",
          differentiation: "Mock rationale for differentiation",
          feel: "Mock rationale for feel",
          internationality: "Mock rationale for internationality",
          technical_aesthetics: "Mock rationale for technical aesthetics",
        },
        verdict,
      }
    });
  }
  
  return { names };
};

// Mock data for /check-domain endpoint
export const mockCheckDomain = (params: {
  name: string;
  tld: string;
}): { available: boolean } => {
  // Create deterministic but varied availability
  // Hash the domain name to get consistent results
  const hash = params.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  // 40% of domains are available
  const available = hash % 10 < 4;
  
  return { available };
};

// Mock data for /search-trademarks endpoint
export const mockSearchTrademarks = (params: {
  names: string[];
}): { results: Record<string, boolean> } => {
  const results: Record<string, boolean> = {};
  
  params.names.forEach(name => {
    // Hash-based deterministic trademark availability
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    // false means trademark is NOT found (i.e., available)
    // true means trademark IS found (i.e., not available)
    // 70% of trademarks are available (not found)
    results[name] = hash % 10 >= 7;
  });
  
  return { results };
};

